import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';

import cn from 'classnames';
import * as dt from 'core/datetime';
import { DATE_FORMAT } from 'globals/constants';

import { ReactComponent as ChevronRightIcon } from 'assets/chevron_right.svg';
import { ReactComponent as CheckIcon } from 'assets/green-check.svg';
import { ReactComponent as TrashIcon } from 'assets/trash.svg';

import PollParticipations from './PollParticipations';
import RemoveExportedActivityDialog from './RemoveExportedActivityDialog';

/**
 * Returns a color class name for a number between 0 and 100, returns empty string otherwise
 * @param percentage attendance percentage
 */
const getColorClassForPercentage = (percentage: number) => {
  if (percentage >= 80) return 'table__cell-value--green';
  else if (percentage >= 60) return 'table__cell-value--orange';
  return 'table__cell-value--red';
};

interface UpdateExportStatusAction {
  activityId: string;
  isExported: boolean;
}

const updateExportStatusReducer = (state: HashMap<boolean>, action: UpdateExportStatusAction) => {
  return {
    ...state,
    [action.activityId]: action.isExported,
  };
};

interface PollScoresProps {
  data?: ActivityScore[];
}

const PollScores: FC<PollScoresProps> = ({ data }) => {
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [exportStatusMap, updateExportStatus] = useReducer(updateExportStatusReducer, {} as HashMap<boolean>);
  const [isRemoveExportedActivityDialogOpen, setRemoveExportedActivityDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityScore | null>(null);
  const [hoveredActivityId, setHoveredActivityId] = useState('');

  const openRemoveExportedActivityDialog = (a: ActivityScore) => {
    setSelectedActivity(a);
    setRemoveExportedActivityDialogOpen(true);
  };

  const closeRemoveExportedActivityDialog = () => {
    setSelectedActivity(null);
    setRemoveExportedActivityDialogOpen(false);
  };

  const handleRemoveExport = useCallback(() => {
    if (!selectedActivity) return;
    updateExportStatus({
      activityId: selectedActivity?.activityId,
      isExported: false,
    });
    closeRemoveExportedActivityDialog();
  }, [selectedActivity]);

  const handleHoverEvent = (id: string) => () => {
    setHoveredActivityId(id);
  };

  useEffect(
    () =>
      data?.forEach((a) => updateExportStatus({ activityId: a.activityId, isExported: a.exported === 1 })),
    [data]
  );

  const openActivityDetails = useCallback((activityId: string) => {
    setSelectedActivityId(activityId);
    setIsActivityDialogOpen(true);
  }, []);

  if (!data?.length) {
    return (
      <div className='activities'>
        <div className='activities__title'>Poll Scores</div>
        <p className='activity__no-data'>No polls published yet</p>
      </div>
    );
  }

  return (
    <div className='activities'>
      <div className='activities__title'>Poll Scores</div>
      <table className='table table--bordered activity__score-table'>
        <tbody>
          {data.map((a, i) => (
            <tr
              key={i}
              className={cn('gutters', 'clickable', 'backgroundColorWhite')}
              onMouseEnter={exportStatusMap[a.activityId] ? handleHoverEvent(a.activityId) : undefined}
              onMouseLeave={exportStatusMap[a.activityId] ? handleHoverEvent('') : undefined}
              onClick={() =>
                exportStatusMap[a.activityId]
                  ? openRemoveExportedActivityDialog(a)
                  : openActivityDetails(a.activityId)
              }
            >
              <td>
                <div className='table__cell'>
                  <div className='table__cell-header table__cell-header--title'>{`Poll ${a.activityNum}`}</div>
                  <div className='table__cell-value table__cell-value--sm'>{a.title}</div>
                </div>
              </td>
              <td>
                <div className='table__cell'>
                  <div
                    className={cn('tag', {
                      'tag--green': a.toBeDone === 'inClass',
                      'tag--blue': a.toBeDone === 'preClass',
                    })}
                  >
                    {a.toBeDone === 'inClass' ? 'IN CLASS' : 'PRE CLASS'}
                  </div>
                  <div className='table__cell-value table__cell-value--no-wrap'>
                    {dt.format(a.dueDateTime, DATE_FORMAT)}
                  </div>
                </div>
              </td>
              <td>
                <div className='table__cell'>
                  <div className='table__cell-header'>SUBMISSIONS</div>
                  <div className='table__cell-value'>{a.numSubmissions}</div>
                </div>
              </td>
              <td>
                <div className='table__cell'>
                  <div className='table__cell-header'>PARTICIPATION</div>
                  <div>
                    {a.hasScore ? (
                      <span className={getColorClassForPercentage(a.avgScore)}>{a.avgScore}%</span>
                    ) : (
                      '--'
                    )}
                  </div>
                </div>
              </td>
              <td className='has-tooltip'>
                {exportStatusMap[a.activityId] ? (
                  <div className='table__cell tooltip-trigger left' data-tooltip='Already exported'>
                    {hoveredActivityId === a.activityId ? (
                      <TrashIcon display='block' />
                    ) : (
                      <CheckIcon display='block' />
                    )}
                  </div>
                ) : (
                  <div className='table__cell pointer tooltip-trigger left' data-tooltip='Export data'>
                    <ChevronRightIcon display='block' />
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PollParticipations
        isOpen={isActivityDialogOpen}
        activityId={selectedActivityId}
        onExport={() => {
          updateExportStatus({
            activityId: selectedActivityId,
            isExported: true,
          });
          setIsActivityDialogOpen(false);
        }}
        onClose={() => setIsActivityDialogOpen(false)}
      />
      <RemoveExportedActivityDialog
        isOpen={isRemoveExportedActivityDialogOpen}
        selectedActivity={selectedActivity}
        onClose={closeRemoveExportedActivityDialog}
        onExport={handleRemoveExport}
      />
    </div>
  );
};

export default PollScores;
